// App «Configuración» del SO Orion — para TODO usuario (2026-10-28).
//
//  · Mi perfil (cualquiera): cómo te llamo, puesto, acerca de mí, cómo quieres que
//    te hable la IA, nivel de detalle e instrucciones propias. GET/PUT
//    /orion/api/me/preferences (Orión los inyecta en el bloque de personalización
//    del usuario; NO en la capa de la org). Vista previa transparente de lo que la
//    IA sabe de ti.
//  · Prompt de la org (admin): el editor de siempre (capa `base`, versiones +
//    restaurar) — PromptEditorPage embebido.
//  · Más ajustes (admin): accesos a Empresa/Config que ya existen (nada se pierde).
// Ruta: /m/ajustes/inicio?s=perfil|prompt|mas — misma app en ambos shells.

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bell, BookOpen, Bot, Building2, ChevronRight, Eye, LayoutGrid, Layers, Loader2, Settings, SlidersHorizontal, UserRound, Wand2, Zap,
} from 'lucide-react';

import { apiUrl } from '@/config';
import { ensureFreshToken } from '@/lib/tokenRefresh';
import { useModules } from '@/context/ModulesContext';
import { OsApp, OsTabs } from '@/modules/orion-shell/OsApp';
import { canOpen, toPath, type Access } from '@/modules/orion-shell/nav';
import { PromptEditorPage } from '@/modules/agent-prompt/PromptEditorPage';

type Section = 'perfil' | 'prompt' | 'mas';
type Notify = (n: { type: 'success' | 'error' | 'info' | 'warning'; title: string; message?: string }) => void;

export function AjustesApp({ addNotification }: { addNotification?: Notify }) {
  const { availableModules, isAdmin, isDev, sovereign } = useModules();
  const admin = isAdmin || isDev;
  const [sp, setSp] = useSearchParams();
  const requested = sp.get('s') as Section | null;
  const section: Section = !admin ? 'perfil' : requested && ['perfil', 'prompt', 'mas'].includes(requested) ? requested : 'perfil';
  const go = (s: Section) => {
    sp.set('s', s);
    setSp(sp, { replace: true });
  };

  const access: Access = { availableModules, isAdmin, isDev, sovereign };
  const tabs = [
    { key: 'perfil' as const, label: 'Mi perfil', icon: UserRound },
    ...(admin
      ? [
          { key: 'prompt' as const, label: 'Prompt', icon: Wand2 },
          { key: 'mas' as const, label: 'Más ajustes', icon: SlidersHorizontal },
        ]
      : []),
  ];

  return (
    <div className="mx-auto w-full max-w-6xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
      <OsApp
        icon={Settings}
        title="Configuración"
        subtitle={admin ? 'Tu perfil y los ajustes de la organización' : 'Tu perfil y cómo te habla la IA'}
        tabs={tabs.length > 1 ? <OsTabs value={section} onChange={go} items={tabs} label="Secciones de configuración" /> : undefined}
      >
        {section === 'perfil' && <PerfilPanel addNotification={addNotification} />}
        {section === 'prompt' && admin && (
          <div>
            <h2 className="text-base font-semibold text-foreground">Prompt de la organización</h2>
            <p className="mb-3 mt-0.5 max-w-3xl text-sm text-muted-foreground">
              Es la instrucción base del agente de tu organización: quiénes son, qué venden, cómo hablan y qué nunca debe decir. Cada
              guardado crea una versión nueva (efecto al siguiente mensaje) y puedes restaurar cualquiera.
            </p>
            <PromptEditorPage embedded />
          </div>
        )}
        {section === 'mas' && admin && <MasAjustes access={access} />}
      </OsApp>
    </div>
  );
}

// ── Mi perfil ─────────────────────────────────────────────────

interface Prefs {
  preferred_name: string;
  job_title: string;
  about_me: string;
  response_style: string;
  detail_level: string;
  custom_instructions: string;
}

const EMPTY: Prefs = { preferred_name: '', job_title: '', about_me: '', response_style: '', detail_level: '', custom_instructions: '' };

// Mismos valores que PersonalizationPanel (/chat) → el backend los recibe igual.
const STYLES = [
  { value: '', label: 'Automático', hint: 'Orión decide según la conversación' },
  { value: 'amigable', label: 'Amigable', hint: 'Cercano y cálido' },
  { value: 'formal', label: 'Formal', hint: 'Profesional, de usted' },
  { value: 'conciso', label: 'Conciso', hint: 'Lo esencial, sin rodeos' },
  { value: 'directo', label: 'Directo', hint: 'Recomendación primero' },
  { value: 'didactico', label: 'Didáctico', hint: 'Explica paso a paso' },
];

const DETAIL = [
  { value: '', label: 'Automático' },
  { value: 'breve', label: 'Breve' },
  { value: 'equilibrado', label: 'Equilibrado' },
  { value: 'detallado', label: 'Detallado' },
];

const PUESTOS = ['Dirección general', 'Ventas', 'Marketing', 'Operaciones', 'Finanzas', 'Atención a clientes', 'Recursos humanos', 'Sistemas'];

async function authHeaders(): Promise<Record<string, string>> {
  await ensureFreshToken();
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('auth_token') || ''}` };
}

function PerfilPanel({ addNotification }: { addNotification?: Notify }) {
  const [p, setP] = useState<Prefs>(EMPTY);
  const [saved, setSaved] = useState<Prefs>(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'ok' | 'warn' | 'error'; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(apiUrl('/me/preferences'), { headers: await authHeaders() });
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const got = data?.preferences || {};
        const next: Prefs = { ...EMPTY };
        (Object.keys(EMPTY) as (keyof Prefs)[]).forEach((k) => (next[k] = got[k] || ''));
        if (!cancelled) {
          setP(next);
          setSaved(next);
        }
      } catch {
        if (!cancelled) setMsg({ kind: 'warn', text: 'No pude cargar tu perfil. Puedes llenarlo y guardarlo de nuevo.' });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const set = <K extends keyof Prefs>(k: K, v: Prefs[K]) => setP((x) => ({ ...x, [k]: v }));
  const dirty = (Object.keys(EMPTY) as (keyof Prefs)[]).some((k) => p[k].trim() !== saved[k].trim());

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const res = await fetch(apiUrl('/me/preferences'), { method: 'PUT', headers: await authHeaders(), body: JSON.stringify(p) });
      if (!res.ok) throw new Error(String(res.status));
      const data = await res.json();
      const got = data?.preferences || {};
      const next: Prefs = { ...EMPTY };
      (Object.keys(EMPTY) as (keyof Prefs)[]).forEach((k) => (next[k] = got[k] || ''));
      setP(next);
      setSaved(next);
      // Servidor sin la versión del perfil: guarda tono/acerca de mí, ignora lo nuevo.
      if (!('job_title' in got)) {
        setMsg({ kind: 'warn', text: 'Se guardó tu tono y «acerca de mí». El puesto, el nombre y el nivel de detalle se activan con la próxima actualización del servidor.' });
      } else {
        setMsg({ kind: 'ok', text: 'Listo. Orión lo usa desde tu siguiente mensaje.' });
        addNotification?.({ type: 'success', title: 'Perfil guardado' });
      }
    } catch {
      setMsg({ kind: 'error', text: 'No se pudo guardar. Intenta de nuevo en un momento.' });
    } finally {
      setSaving(false);
    }
  };

  const preview = useMemo(() => {
    const out: string[] = [];
    if (p.preferred_name.trim()) out.push(`Te llama «${p.preferred_name.trim()}».`);
    if (p.job_title.trim()) out.push(`Sabe que tu puesto es ${p.job_title.trim()} y adapta ejemplos y prioridades a eso.`);
    const st = STYLES.find((s) => s.value === p.response_style);
    if (p.response_style && st) out.push(`Te habla en tono ${st.label.toLowerCase()} (${st.hint.toLowerCase()}).`);
    if (p.detail_level) out.push(`Respuestas con nivel de detalle ${p.detail_level}.`);
    if (p.about_me.trim()) out.push('Tiene en cuenta lo que le contaste de ti.');
    if (p.custom_instructions.trim()) out.push('Sigue tus instrucciones personales.');
    return out;
  }, [p]);

  if (loading) {
    return (
      <p role="status" className="flex items-center gap-2 py-8 text-sm text-muted-foreground">
        <Loader2 size={16} className="animate-spin" aria-hidden /> Cargando tu perfil…
      </p>
    );
  }

  const input = 'h-10 w-full rounded-xl border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring';

  return (
    <form
      className="grid gap-5 lg:grid-cols-[1fr_320px]"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <div className="grid min-w-0 gap-5">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">¿Cómo quieres que te llame?</span>
            <input value={p.preferred_name} onChange={(e) => set('preferred_name', e.target.value)} maxLength={60} placeholder="Ej. Sam, Lic. Ramírez" className={input} />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-muted-foreground">Puesto</span>
            <input value={p.job_title} onChange={(e) => set('job_title', e.target.value)} maxLength={120} list="ajustes-puestos" placeholder="Ej. Director comercial" className={input} />
            <datalist id="ajustes-puestos">
              {PUESTOS.map((x) => <option key={x} value={x} />)}
            </datalist>
          </label>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Acerca de mí</span>
          <textarea
            value={p.about_me}
            onChange={(e) => set('about_me', e.target.value)}
            rows={3}
            maxLength={1500}
            placeholder="A qué te dedicas, qué decisiones tomas y qué te importa. Ej. «Llevo ventas de 3 sucursales; reviso números los lunes»."
            className="w-full resize-y rounded-xl border border-input bg-card px-3 py-2.5 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        <fieldset>
          <legend className="mb-1.5 text-xs font-medium text-muted-foreground">¿Cómo quieres que te hable la IA?</legend>
          <div role="radiogroup" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {STYLES.map((s) => (
              <button
                key={s.value || 'auto'}
                type="button"
                role="radio"
                aria-checked={p.response_style === s.value}
                onClick={() => set('response_style', s.value)}
                className={`rounded-xl border px-3 py-2 text-left transition-colors ${
                  p.response_style === s.value ? 'border-primary bg-primary/10' : 'border-border bg-card hover:border-primary/40'
                }`}
              >
                <span className="block text-sm font-medium text-foreground">{s.label}</span>
                <span className="block text-[11.5px] text-muted-foreground">{s.hint}</span>
              </button>
            ))}
          </div>
        </fieldset>

        <fieldset>
          <legend className="mb-1.5 text-xs font-medium text-muted-foreground">Nivel de detalle</legend>
          <div role="radiogroup" className="grid grid-cols-4 rounded-xl bg-muted p-1 sm:inline-grid">
            {DETAIL.map((dl) => (
              <button
                key={dl.value || 'auto'}
                type="button"
                role="radio"
                aria-checked={p.detail_level === dl.value}
                onClick={() => set('detail_level', dl.value)}
                className={`h-8 rounded-lg px-1.5 text-[12.5px] font-medium transition-colors sm:px-3 sm:text-[13px] ${
                  p.detail_level === dl.value ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {dl.label}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="block">
          <span className="mb-1 block text-xs font-medium text-muted-foreground">Instrucciones personales para la IA (opcional)</span>
          <textarea
            value={p.custom_instructions}
            onChange={(e) => set('custom_instructions', e.target.value)}
            rows={3}
            maxLength={3000}
            placeholder="Ej. «Dame primero la recomendación y luego el porqué. Montos siempre en pesos»."
            className="w-full resize-y rounded-xl border border-input bg-card px-3 py-2.5 text-sm leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        {msg && (
          <p
            role={msg.kind === 'error' ? 'alert' : 'status'}
            className={`rounded-lg px-3 py-2 text-xs ${
              msg.kind === 'ok' ? 'bg-primary/10 text-primary' : msg.kind === 'warn' ? 'bg-muted text-foreground' : 'bg-destructive/10 text-destructive'
            }`}
          >
            {msg.text}
          </p>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-4">
          {dirty && (
            <button type="button" onClick={() => setP(saved)} className="h-9 rounded-xl px-3 text-sm font-medium text-foreground hover:bg-muted">
              Descartar cambios
            </button>
          )}
          <button type="submit" disabled={!dirty || saving} className="os-btn-brand inline-flex h-9 items-center gap-1.5 rounded-xl px-4 text-sm font-semibold">
            {saving && <Loader2 size={15} className="animate-spin" aria-hidden />} Guardar perfil
          </button>
        </div>
      </div>

      <aside aria-labelledby="ajustes-preview" className="h-fit rounded-2xl border border-border bg-card p-4 lg:sticky lg:top-4">
        <h2 id="ajustes-preview" className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <Eye size={15} aria-hidden className="text-primary" /> Lo que la IA sabe de ti
        </h2>
        {preview.length === 0 ? (
          <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
            Aún nada. Llena tu perfil y Orión adaptará sus respuestas a ti en todas tus conversaciones.
          </p>
        ) : (
          <ul className="mt-2 grid gap-1.5 text-[13px] leading-relaxed text-foreground">
            {preview.map((x) => (
              <li key={x} className="flex gap-2">
                <span aria-hidden className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                {x}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 border-t border-border pt-3 text-[11.5px] leading-relaxed text-muted-foreground">
          Es tuyo: solo se usa en tus conversaciones con la IA y lo cambias o borras cuando quieras. No escribas contraseñas ni datos
          de salud.
        </p>
      </aside>
    </form>
  );
}

// ── Más ajustes (lo que ya existía, a un clic) ─────────────────

const MAS: { target: string; title: string; desc: string; icon: JSX.Element }[] = [
  { target: 'empresa/ai-agent', title: 'Agente IA', desc: 'Nombre, foto y modelo del agente de la organización.', icon: <Bot size={18} /> },
  { target: 'empresa/modulos', title: 'Módulos', desc: 'Qué apps tiene encendidas tu organización.', icon: <LayoutGrid size={18} /> },
  { target: 'empresa/company', title: 'Datos de la empresa', desc: 'Información, llaves de servicios y facturación.', icon: <Building2 size={18} /> },
  { target: 'empresa/notificaciones', title: 'Notificaciones', desc: 'Quién recibe avisos y por qué canal.', icon: <Bell size={18} /> },
  { target: 'config/prompts', title: 'Prompts avanzados', desc: 'Todas las capas del agente (conciencia, base, comercial).', icon: <Layers size={18} /> },
  { target: 'config/workflows', title: 'Workflows', desc: 'Automatizaciones de la organización.', icon: <Zap size={18} /> },
  { target: 'config/guide', title: 'Guía de uso', desc: 'Cómo sacarle provecho a Orion.', icon: <BookOpen size={18} /> },
];

function MasAjustes({ access }: { access: Access }) {
  const navigate = useNavigate();
  const items = MAS.filter((m) => canOpen(m.target, access));
  if (items.length === 0) {
    return <p className="py-6 text-sm text-muted-foreground">Los demás ajustes de tu organización los administra el equipo de Orion.</p>;
  }
  return (
    <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {items.map((m) => (
        <li key={m.target}>
          <a
            href={toPath(m.target)}
            onClick={(e) => {
              e.preventDefault();
              navigate(toPath(m.target));
            }}
            className="orion-card group flex h-full items-start gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary/40"
          >
            <span aria-hidden className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary">{m.icon}</span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-foreground">{m.title}</span>
              <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">{m.desc}</span>
            </span>
            <ChevronRight size={16} aria-hidden className="mt-1 shrink-0 text-muted-foreground group-hover:text-foreground" />
          </a>
        </li>
      ))}
    </ul>
  );
}

export default AjustesApp;
